// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC, useMemo, useEffect } from "react";

import { Column, Row, Select } from "@hightouch/htui";
import { v4 as uuidv4 } from "uuid";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  Condition,
  ConditionType,
  SegmentSetCondition,
  segmentSetOperatorOptions,
} from "src/types/visual";

import { FilterPopover } from "src/components/explore/filter-popover";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { FilterProps } from "./condition";
import { validateSegmentSetCondition } from "./condition-validation";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { FieldError } from "src/components/field-error";

export const applySegmentSetConditionChange = (
  previous: SegmentSetCondition,
  next: Condition,
): Condition => {
  // if the next condition is also a segment set, that means we are swapping the segment selected
  // carry over the `includes` value instead of letting it reset to true to avoid confusion.
  return next.type === ConditionType.SegmentSet
    ? { ...next, includes: previous.includes }
    : next;
};

export const SegmentSetFilter: FC<
  Readonly<FilterProps<SegmentSetCondition>>
> = ({ condition, onUpdate, ...props }) => {
  const filterId = useMemo<string>(uuidv4, []);

  const { enableRealtime } = useQueryBuilderContext();
  const { rtaWritebackReadsIsEnabled = false } = useFlags();
  const { relatedAudiences, parentSnapshottingEnabled, isParentModelLoading } =
    useFilterableOptionsContext();
  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();

  const filterErrors = getErrors(filterId, { allErrors: true });
  const modelError = filterErrors?.modelId;
  const includesError = filterErrors?.includes;
  const conditionError = filterErrors?.condition;

  const referencedAudience = useMemo(() => {
    if (condition.modelId == null) {
      return undefined;
    }

    const audience = relatedAudiences.find(
      (entry) => String(entry.id) === String(condition.modelId),
    );
    if (!audience) {
      return undefined;
    }

    return audience.is_realtime_audience
      ? {
          isRealtimeAudience: true as const,
          parentSnapshottingEnabled,
        }
      : {
          isRealtimeAudience: false as const,
          parentSnapshottingEnabled,
        };
  }, [condition.modelId, relatedAudiences, parentSnapshottingEnabled]);

  useEffect(() => {
    if (isParentModelLoading) {
      return () => {
        removeErrors([filterId]);
      };
    }

    setFieldError(
      filterId,
      validateSegmentSetCondition(condition, {
        isRealtimeAudience: enableRealtime,
        rtaWritebackReadsIsEnabled,
        referencedAudience,
      }),
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [
    condition,
    filterId,
    enableRealtime,
    rtaWritebackReadsIsEnabled,
    referencedAudience,
    isParentModelLoading,
    setFieldError,
    removeErrors,
  ]);

  return (
    <Column>
      <Row
        alignItems="flex-start"
        gap={2}
        sx={{ button: { fontWeight: "medium" } }}
      >
        <Select
          removePortal
          isInvalid={Boolean(includesError)}
          options={segmentSetOperatorOptions}
          value={condition.includes}
          width="auto"
          onChange={(value) =>
            onUpdate((condition) => {
              condition.includes = Boolean(value);
            })
          }
        />
        <FilterPopover
          {...props}
          condition={condition}
          hasError={Boolean(modelError)}
          onChange={(newCondition) =>
            onUpdate((previous) =>
              applySegmentSetConditionChange(previous, newCondition),
            )
          }
        />
      </Row>
      {modelError && <FieldError error={modelError} />}
      {conditionError && <FieldError error={conditionError} />}
    </Column>
  );
};
